import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import useShowReelTemplates, {
  Template
} from "../../../hooks/useShowReelTemplates";

interface GenerateShowreelModalProps {
  searchParams: Record<string, any>;
  personName: string;
  organizationId?: number;
  onClose: () => void;
}

type Phase =
  | "select"
  | "generating"
  | "done"
  | "no-results"
  | "insufficient"
  | "error";

interface Asset {
  format: string | null;
  signed_url: string;
}

const POLL_INTERVAL_MS = 5000;
const LIBRARY_URL = "/showreels";

const FilmIcon = () => (
  <svg width="72" height="60" viewBox="0 0 72 60" fill="none" aria-hidden="true">
    <rect x="1.5" y="1.5" width="69" height="57" rx="6" stroke="#2b1b4d" strokeWidth="3" />
    <rect x="9" y="1.5" width="0" height="57" />
    <path d="M12 1.5v57M60 1.5v57" stroke="#2b1b4d" strokeWidth="3" />
    <path d="M30 20l16 10-16 10V20z" fill="#e6007e" />
  </svg>
);

const GenerateShowreelModal: React.FC<GenerateShowreelModalProps> = ({
  searchParams,
  personName,
  organizationId,
  onClose
}) => {
  const { templates, isLoading: templatesLoading } = useShowReelTemplates(
    organizationId || 0
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [reelName, setReelName] = useState("");
  const [phase, setPhase] = useState<Phase>("select");
  const [reelUrl, setReelUrl] = useState<string>("");
  const [reelDisplayName, setReelDisplayName] = useState<string>("");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const pollStatus = async (id: number) => {
    try {
      const response = await fetch(`/api/v1/showreel_job/${id}/status`);
      if (!response.ok) throw new Error(`status ${response.status}`);

      const data = await response.json();
      if (data.name) setReelDisplayName(data.name);

      if (data.status === "completed") {
        setReelUrl(data.signed_url);
        setAssets(Array.isArray(data.assets) ? data.assets : []);
        setPhase("done");
      } else if (data.status === "no-results") {
        setPhase("no-results");
      } else if (data.status === "insufficient-photos") {
        setPhase("insufficient");
      } else {
        pollRef.current = setTimeout(() => pollStatus(id), POLL_INTERVAL_MS);
      }
    } catch (error) {
      console.error("Showreel status error:", error);
      setPhase("error");
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplateId) return;
    setPhase("generating");

    try {
      const response = await fetch("/api/v1/showreels/generate_from_search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_id: selectedTemplateId,
          ...(reelName.trim() && { name: reelName.trim() }),
          ...searchParams
        })
      });

      if (!response.ok) throw new Error(`generate ${response.status}`);

      const data = await response.json();
      pollStatus(data.showreel_job_id);
    } catch (error) {
      console.error("Showreel generate error:", error);
      setPhase("error");
    }
  };

  const libraryFooter = (
    <div className="showreel-modal-footer-band">
      <p>
        All the generated show reels can be accessed in your{" "}
        <a href={LIBRARY_URL}>Show Reels library</a>.
      </p>
      <a
        className="geo-btn geo-btn--secondary geo-btn--caps"
        href={LIBRARY_URL}
      >
        Go to the Showreels page
      </a>
    </div>
  );

  const renderBody = () => {
    if (phase === "generating") {
      return (
        <div className="showreel-modal-status">
          <FilmIcon />
          <h3 className="showreel-modal-heading">Processing Showreel</h3>
          <p>
            Your showreel is being generated in the background, this window can be
            closed.
          </p>
          <p className="showreel-modal-note">
            You&apos;ll find it in your Show Reels library when it&apos;s ready.
          </p>
          <a
            className="geo-btn geo-btn--secondary geo-btn--caps"
            href={LIBRARY_URL}
          >
            Go to the Showreels page
          </a>
        </div>
      );
    }

    if (phase === "done") {
      const downloads: Asset[] =
        assets.length > 0 ? assets : [{ format: null, signed_url: reelUrl }];

      return (
        <>
          <div className="showreel-modal-status">
            <h3 className="showreel-modal-heading">Showreel is ready!</h3>
            <video className="showreel-modal-player" src={reelUrl} controls />
            <p className="showreel-modal-caption">
              {reelDisplayName || `${personName}'s showreel`}
            </p>

            <div className="showreel-download">
              <button
                type="button"
                className="geo-btn geo-btn--primary geo-btn--caps showreel-download__btn"
                onClick={() => setDownloadOpen(open => !open)}
              >
                Download <i className="fa fa-chevron-down" />
              </button>
              {downloadOpen && (
                <div className="showreel-download__menu">
                  {downloads.map(asset => (
                    <a
                      key={asset.format || "reel"}
                      href={asset.signed_url}
                      download
                      onClick={() => setDownloadOpen(false)}
                    >
                      {asset.format
                        ? asset.format.charAt(0).toUpperCase() +
                          asset.format.slice(1)
                        : "Download"}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          {libraryFooter}
        </>
      );
    }

    if (phase === "insufficient") {
      return (
        <div className="showreel-modal-status">
          <p>
            Not enough photos yet to make a showreel for {personName}. Try again
            once more photos are available.
          </p>
        </div>
      );
    }

    if (phase === "no-results") {
      return (
        <div className="showreel-modal-status">
          <p>
            No usable photos or videos were found for {personName}. If the
            template sets a minimum face size, try lowering it.
          </p>
        </div>
      );
    }

    if (phase === "error") {
      return (
        <div className="showreel-modal-status">
          <p>Something went wrong generating the showreel. Please try again.</p>
        </div>
      );
    }

    if (templatesLoading) {
      return (
        <div className="showreel-modal-status">
          <div className="showreel-modal-spinner" />
        </div>
      );
    }

    if (templates.length === 0) {
      return (
        <div className="showreel-modal-status">
          <p>No showreel templates are set up yet.</p>
        </div>
      );
    }

    return (
      <>
        <div className="showreel-template-list">
          {templates.map((template: Template) => (
            <label key={template.id} className="showreel-template-option">
              <input
                type="radio"
                name="showreel-template"
                value={template.id}
                checked={selectedTemplateId === template.id}
                onChange={() => setSelectedTemplateId(template.id)}
              />
              {template.coverImageURL && (
                <img
                  className="showreel-template-cover"
                  src={template.coverImageURL}
                  alt={template.name}
                />
              )}
              <div className="showreel-template-meta">
                <span className="showreel-template-name">{template.name}</span>
                {template.description && (
                  <span className="showreel-template-description">
                    {template.description}
                  </span>
                )}
              </div>
            </label>
          ))}
        </div>
        <input
          type="text"
          className="showreel-modal-name"
          placeholder="Showreel name (optional)"
          value={reelName}
          onChange={e => setReelName(e.target.value)}
        />
        <p className="showreel-modal-note">
          A showreel is built from the content configured in the chosen template
          (its own events and tags) and automatically excludes redacted and
          hidden photos — so it may include different or fewer items than the
          results shown here.
        </p>
      </>
    );
  };

  return ReactDOM.createPortal(
    <div className="showreel-modal-overlay" onClick={onClose}>
      <div className="showreel-modal" onClick={e => e.stopPropagation()}>
        <div className="showreel-modal-header">
          {phase === "select" ? <h3>Create showreel</h3> : <span />}
          <button
            type="button"
            className="showreel-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="showreel-modal-body">{renderBody()}</div>

        {phase === "select" && templates.length > 0 && (
          <div className="showreel-modal-footer">
            <button
              type="button"
              className="geo-btn geo-btn--secondary geo-btn--caps"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="geo-btn geo-btn--primary geo-btn--caps"
              onClick={handleGenerate}
              disabled={!selectedTemplateId}
            >
              Generate
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default GenerateShowreelModal;
